import React from 'react';
import { Card } from 'react-bootstrap';
import { StatCardSkeleton } from '../solar/StatCard';

/**
 * Vollstandige Skeleton-Ladeansicht fur das PV-Jahresvergleich-Dashboard.
 * Spiegelt exakt alle Bereiche: Header, KPI, Trend-Karte, Tabelle, Diagramm.
 */
export function PvSkeletonLoader() {
    return (
        <div className="pv-dashboard skeleton-loader">
            {/* ——— Kopfbereich Skeleton ——— */}
            <div className="dashboard-header mb-4">
                <SkeletonLine width="60%" height={28} />
                <SkeletonLine width="80%" height={16} />
            </div>

            {/* ——— KPI-Reihe Skeleton (4 Karten) ——— */}
            <div className="row mb-3 g-2">
                <div className="col-6 col-md-3"><StatCardSkeleton /></div>
                <div className="col-6 col-md-3"><StatCardSkeleton variant="progress" /></div>
                <div className="col-6 col-md-3"><StatCardSkeleton variant="specific" /></div>
                <div className="col-6 col-md-3"><StatCardSkeleton variant="energy" /></div>
            </div>

            {/* ——— Trendanalyse-Karte Skeleton ——— */}
            <Card className="mb-3 trend-card">
                <Card.Body>
                    <h6 className="card-title">
                        <i className="bi bi-graph-down-arrow me-1" />
                        Trendanalyse
                    </h6>
                    {/* Bewertungsbanner Skeleton */}
                    <div className="bewertung-banner bewertung-ok mb-3">
                        <SkeletonBox width={40} height={24} radius={999} />
                        <div style={{ flex: 1 }}>
                            <SkeletonLine width="50%" height={18} marginBottom={6} />
                            <SkeletonLine width="85%" height={14} />
                        </div>
                    </div>

                    {/* Status-Zeile Skeleton */}
                    <div className="d-flex justify-content-between mb-2">
                        <SkeletonLine width="30%" height={16} />
                        <SkeletonLine width="40%" height={16} />
                    </div>

                    {/* 3-Metriken Grid Skeleton */}
                    <div className="row g-2 mb-2">
                        <div className="col-4">
                            <SkeletonBox height={55} radius={6} />
                        </div>
                        <div className="col-4">
                            <SkeletonBox height={55} radius={6} />
                        </div>
                        <div className="col-4">
                            <SkeletonBox height={55} radius={6} />
                        </div>
                    </div>

                    {/* Begrundung Skeleton */}
                    <SkeletonLine width="100%" height={14} marginBottom={8} />
                    <SkeletonLine width="75%" height={14} />
                </Card.Body>
            </Card>

            {/* ——— Ausgeschlossene Jahre Skeleton ——— */}
            <Card className="mb-3">
                <Card.Body>
                    <h6 className="card-title">
                        <i className="bi bi-calendar-x me-1" />
                        Ausgeschlossene Jahre
                    </h6>
                    <SkeletonLine width="45%" height={16} marginBottom={8} />
                    <SkeletonLine width="60%" height={16} marginBottom={8} />
                    <SkeletonLine width="35%" height={16} />
                </Card.Body>
            </Card>

            {/* ——— Tabelle Skeleton ——— */}
            <Card className="mb-3">
                <Card.Body>
                    <h6 className="card-title">
                        <i className="bi bi-table me-1" />
                        Jahreswerte-Ubersicht
                    </h6>
                    <div className="table-responsive">
                        <SkeletonTable rows={8} columns={8} />
                    </div>
                </Card.Body>
            </Card>

            {/* ——— Diagramm Skeleton ——— */}
            <Card className="mb-3 chart-card">
                <Card.Body>
                    {/* Button-Umschalter Skeleton */}
                    <div className="d-flex justify-content-center gap-2 mb-3">
                        <SkeletonBox width={140} height={32} radius={6} />
                        <SkeletonBox width={150} height={32} radius={6} />
                        <SkeletonBox width={130} height={32} radius={6} />
                    </div>
                    {/* Chart-Bereich Skeleton */}
                    <SkeletonBox height={400} radius={8} />
                </Card.Body>
            </Card>

            {/* ——— Footer Skeleton ——— */}
            <footer className="dashboard-footer text-muted mt-4">
                <SkeletonLine width="50%" height={12} />
            </footer>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Primitive Skeleton-Komponenten                                     */
/* ------------------------------------------------------------------ */

function SkeletonLine({ width, height, marginBottom }) {
    return (
        <div
            className="skeleton-line"
            style={{
                width: width || '100%',
                height: height || 14,
                marginBottom: marginBottom || 6,
            }}
        />
    );
}

function SkeletonBox({ width, height, radius }) {
    return (
        <div
            className="skeleton-box"
            style={{
                width: width || '100%',
                height: height,
                borderRadius: radius !== undefined ? radius : 6,
            }}
        />
    );
}

function SkeletonTable({ rows, columns }) {
    return (
        <table className="table table-sm table-bordered mb-0">
            <thead>
                <tr>
                    {Array.from({ length: columns }).map((_, i) => (
                        <th key={i}>
                            <SkeletonLine width="80%" height={12} />
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: rows }).map((_, r) => (
                    <tr key={r}>
                        {Array.from({ length: columns }).map((_, c) => (
                            <td key={c}>
                                <SkeletonLine width="60%" height={12} />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
